/**
 * Track 1 Telemetry - bounded, gated, PII-redacted, in-memory ring buffer.
 *
 * DO NOT use src/utils/devLog.js for Track 1 events: devLog is gated on
 * NODE_ENV !== 'production' and CRA staging builds set NODE_ENV=production,
 * so devLog swallows silently on staging - the exact failure mode we're hunting.
 *
 * Gate:
 *   Build-time: REACT_APP_ENABLE_TRACK1_INSTRUMENTATION === 'true'
 *               (set only on staging Dockerfile via --build-arg)
 *   Runtime:    localStorage['sr.track1.on'] === 'true'
 *               (advisor / investigator override for prod-hunt sessions)
 *   Either enables telemetry. Both false = no-op.
 *
 * PII policy (Author B BLOCKER):
 *   - Free-text fields (assignedTo, name, item, createdBy, updatedBy, archivedBy)
 *     NEVER logged as raw strings. Replaced by `has_<field>: boolean` +
 *     `<field>_len: number`.
 *   - IDs hashed via inline fnv1a (~10 LOC) -> 8 hex chars. Enables cross-log
 *     correlation without exposing raw org/vessel/job ids.
 *   - Any string >32 chars truncated to first 8 + '…' + last 4.
 *   - Payload capped at 2 KB per entry (truncate + mark).
 *
 * Ring buffer (Author B BLOCKER):
 *   - In-memory Array, capacity 500, oldest evicted.
 *   - ~1 MB retained max (payload cap * capacity).
 *   - NEVER written to localStorage / IndexedDB.
 *
 * Export:
 *   - window.__track1.dump() returns JSON string with warning prefix.
 *   - Only defined when IS_TRACK1_ON() at boot; undefined otherwise (prod safety).
 *
 * Safety:
 *   - Every entry point wrapped in try/catch - telemetry never crashes a save.
 *   - Zero external HTTP calls.
 *   - No React hooks - module-level singleton with stable identity.
 *
 * Known event names (for future greppers):
 *   - [SILENT-DROP], [SYNC-HYDRATE], [SR-STORAGE-SET], [SAVE-BEGIN], [SAVE-END],
 *     [FAN-OUT], [NETWORK-STATE], [SYNC-MERGE-DECISION]
 *
 * FT-379 anomaly path (ALWAYS-ON, gate-independent):
 *   - A small (~50-entry, oldest-evict) slice persisted SYNCHRONOUSLY to
 *     localStorage['sr.track1.anomalies'] so a data-loss event survives the
 *     reload it usually rides in on. Distinct from the in-memory ring above.
 *   - Only these 4 event types are captured + shipped: [PULL-CLOBBER]
 *     (real-loss only, localNewerLost===true), [SILENT-DROP],
 *     [SYNC-QUEUE-USER-MISMATCH], [SYNC-ABORT-DROP].
 *   - track1.logAnomaly() persists + queues even when the main gate is off.
 *   - track1.flush() POSTs the batched queue to POST {API}/telemetry/sync-events
 *     (bearer JWT; server derives org/user), draining LS only on 2xx. Called
 *     fire-and-forget on boot + reconnect. PII-safe: no record content ever.
 *   - Retrieval: window.__track1anomaly.list()/clear()/flush() (gate-independent)
 *     + window.__track1.anomalies()/clearAnomalies() (gated).
 */

const CAPACITY = 500;
const PAYLOAD_CAP_BYTES = 2048;
const STRING_TRUNC_LIMIT = 32;

// ── FT-379: sync-integrity anomaly observability ──────────────────────────
// A SMALL, always-on, PII-safe slice that survives the reload a data-loss
// event usually rides in on. Distinct from the 500-entry in-memory ring:
//   - persisted synchronously to localStorage (survives teardown; IDB can lose
//     an in-flight txn on unload — brief §Part1.3),
//   - bounded (oldest-evict),
//   - captured + queued EVEN WHEN THE MAIN GATE IS OFF (brief §Part1.4),
//   - drained to the server sink opportunistically (brief §Part1.5).
// Nothing here ships record CONTENT — only app identifiers (collection,
// hashed org/vessel/item ids), timestamps, booleans, and event type.
const ANOMALY_LS_KEY = 'sr.track1.anomalies';
const ANOMALY_LS_CAP = 50;              // oldest-evict beyond this
const ANOMALY_DEVICE_ID_KEY = 'sr.track1.deviceId';
const ANOMALY_FLUSH_BATCH = 100;        // server contract cap per POST
const ANOMALY_SINK_PATH = '/telemetry/sync-events';
const ANOMALY_AUTH_TOKEN_KEY = 'sms_pro_auth_token'; // mirrors azureApiClient TOKEN_KEY

// The ONLY 4 event types shipped to the sink (brief allowlist). PULL-CLOBBER
// is captured always-on only when it represents a real loss (localNewerLost).
const ANOMALY_ALLOWLIST = new Set([
  '[PULL-CLOBBER]',
  '[SILENT-DROP]',
  '[SYNC-QUEUE-USER-MISMATCH]',
  '[SYNC-ABORT-DROP]',
]);

// Server-contract scalar allowlist for the `details` object. Belt-and-braces:
// the server strips anything outside its own allowlist too, but we never even
// build a details blob that could carry content.
const ANOMALY_DETAIL_KEYS = [
  'localNewerLost', 'local_has_ts', 'cloud_has_ts',
  'phase', 'reason', 'orgId', 'total', 'staleCount',
];

const IS_TRACK1_ON = () => {
  try {
    if (typeof process !== 'undefined' &&
        process.env &&
        process.env.REACT_APP_ENABLE_TRACK1_INSTRUMENTATION === 'true') {
      return true;
    }
  } catch (_) { /* noop */ }
  try {
    if (typeof localStorage !== 'undefined' &&
        localStorage.getItem('sr.track1.on') === 'true') {
      return true;
    }
  } catch (_) { /* noop */ }
  return false;
};

// fnv1a 32-bit - non-cryptographic hash sufficient for correlation.
const fnv1a = (str) => {
  let h = 0x811c9dc5;
  const s = String(str);
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
};

const truncateString = (s) => {
  if (typeof s !== 'string') return s;
  if (s.length <= STRING_TRUNC_LIMIT) return s;
  return `${s.slice(0, 8)}…${s.slice(-4)}`;
};

// Defence-in-depth (Reviewer B 2026-07-02): future call sites may pass free-text
// fields not currently reached; adding description/notes/requirement/regulation
// pre-emptively so any accidental future logging of raw job records stays safe.
const PII_FIELDS = ['assignedTo', 'name', 'item', 'createdBy', 'updatedBy', 'archivedBy', 'description', 'notes', 'requirement', 'regulation'];
const ID_FIELDS = ['id', 'orgId', 'vesselId', 'sourceTabId', 'receivingTabId', 'jobId', 'originatingDefectId'];

// Whitelist-based redaction. Deep clone via JSON to detach React state
// references (closure-leak guardrail).
const redact = (payload) => {
  if (payload === null || payload === undefined) return payload;
  if (typeof payload !== 'object') return payload;
  let clone;
  try {
    clone = JSON.parse(JSON.stringify(payload));
  } catch (_) {
    return { __err: 'clone-failed' };
  }
  const walk = (node) => {
    if (!node || typeof node !== 'object') return;
    for (const k of Object.keys(node)) {
      const v = node[k];
      // Free-text PII fields - strip.
      if (PII_FIELDS.includes(k)) {
        const wasString = typeof v === 'string';
        delete node[k];
        node[`has_${k}`] = wasString && v.length > 0;
        if (wasString) node[`${k}_len`] = v.length;
        continue;
      }
      // ID-like fields - hash.
      if (ID_FIELDS.includes(k)) {
        if (Array.isArray(v)) node[k] = v.map(fnv1a);
        else if (v != null) node[k] = fnv1a(v);
        continue;
      }
      if (k === 'linkedEquipmentIds' && Array.isArray(v)) {
        node[k] = v.map(fnv1a);
        continue;
      }
      // Truncate long strings.
      if (typeof v === 'string') node[k] = truncateString(v);
      // Recurse.
      if (v && typeof v === 'object') walk(v);
    }
  };
  // Pre-walk size check: guards against oversize raw inputs (single-field blobs)
  // whose per-field string truncation would otherwise shrink them below the cap
  // and hide the fact that a giant payload was passed in.
  let rawInputSize = 0;
  try {
    rawInputSize = JSON.stringify(payload).length;
  } catch (_) {
    /* leave 0 - non-serialisable input still walks + redacts below */
  }
  walk(clone);
  // Post-walk size cap.
  let out;
  try {
    out = JSON.stringify(clone);
  } catch (_) {
    return { __err: 'stringify-failed' };
  }
  const oversize = out.length > PAYLOAD_CAP_BYTES || rawInputSize > PAYLOAD_CAP_BYTES;
  if (oversize) {
    return {
      __truncated: true,
      len: Math.max(out.length, rawInputSize),
      head: out.slice(0, PAYLOAD_CAP_BYTES),
    };
  }
  return clone;
};

// Ring buffer - in-memory only.
const buffer = [];
const push = (entry) => {
  buffer.push(entry);
  if (buffer.length > CAPACITY) buffer.shift();
};

// ── FT-379: anomaly LS persistence + offline queue + flush ────────────────

// Stable, opaque, persisted install id. Reused across sessions so the server
// can group events by device without any PII. Verified (2026-07-16): the app
// has no pre-existing persistent install id — `tabId` in authCacheWipe.js is
// session-scoped (per-tab). So we mint one here and persist it.
const getDeviceId = () => {
  try {
    if (typeof localStorage === 'undefined') return 'unknown';
    let id = localStorage.getItem(ANOMALY_DEVICE_ID_KEY);
    if (!id) {
      id = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `dev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(ANOMALY_DEVICE_ID_KEY, id);
    }
    return id;
  } catch (_) {
    return 'unknown';
  }
};

const getAppVersion = () => {
  try {
    if (typeof process !== 'undefined' && process.env && process.env.REACT_APP_VERSION) {
      return String(process.env.REACT_APP_VERSION);
    }
  } catch (_) { /* noop */ }
  return '1.0.0';
};

const getAuthToken = () => {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(ANOMALY_AUTH_TOKEN_KEY);
  } catch (_) {
    return null;
  }
};

const getApiBase = () => {
  // FT-379 flush-url fix: reference process.env.REACT_APP_AZURE_API_URL DIRECTLY,
  // exactly as src/lib/azureApiClient.js does (`const API_BASE_URL =
  // process.env.REACT_APP_AZURE_API_URL`). CRA/webpack inlines the exact
  // `process.env.REACT_APP_*` member expression as a string literal at BUILD
  // time — it does NOT define a runtime `process` object. The previous
  // `typeof process !== 'undefined'` guard is NOT rewritten by webpack, so in
  // the real browser bundle it evaluated to false at RUNTIME → base resolved to
  // null → flush() early-returned before ever calling fetch (anomalies never
  // shipped). Removing the guard lets the inlined literal be used. In Jest/Node
  // `process.env` genuinely exists, so this reads the same value there too.
  try {
    const base = process.env.REACT_APP_AZURE_API_URL;
    if (base) return base;
  } catch (_) { /* noop — unset env / exotic runtime → null (no-op flush) */ }
  return null;
};

// Read the persisted anomaly slice. PII-safe + gate-independent — reading LS is
// zero-cost and never exposes content, so this works even when the gate is off.
const readAnomalies = () => {
  try {
    if (typeof localStorage === 'undefined') return [];
    const raw = localStorage.getItem(ANOMALY_LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
};

const writeAnomalies = (arr) => {
  try {
    if (typeof localStorage === 'undefined') return;
    // Bounded, oldest-evict: keep only the newest ANOMALY_LS_CAP entries.
    const bounded = arr.length > ANOMALY_LS_CAP ? arr.slice(arr.length - ANOMALY_LS_CAP) : arr;
    localStorage.setItem(ANOMALY_LS_KEY, JSON.stringify(bounded));
  } catch (_) { /* LS full / disabled — swallow; telemetry never breaks the app */ }
};

// Build the exact server-contract event (see BRIEF §"Server contract"). The
// server derives org_id + user_id from the JWT, so we do NOT send them at the
// top level. Only app identifiers, timestamps, booleans, and the event type.
const buildServerEvent = (event, payload) => {
  const p = (payload && typeof payload === 'object') ? payload : {};
  // details: small scalars only, drawn from an allowlist. Never content.
  const details = {};
  for (const k of ANOMALY_DETAIL_KEYS) {
    if (p[k] === undefined || p[k] === null) continue;
    const v = p[k];
    const t = typeof v;
    if (t === 'string' || t === 'number' || t === 'boolean') {
      details[k] = t === 'string' ? truncateString(v) : v;
    }
  }
  return {
    event_type: event,                                   // e.g. '[PULL-CLOBBER]'
    collection: typeof p.collection === 'string' ? p.collection : null,
    item_id: p.id !== undefined ? p.id : null,           // hashed by caller via hashId
    vessel_id: p.vesselId !== undefined ? p.vesselId : null, // hashed by caller
    which_won: typeof p.winner === 'string' ? p.winner : null,
    client_ts: Date.now(),
    local_updated_at: (typeof p.local_ts_norm === 'number') ? p.local_ts_norm : null,
    remote_updated_at: (typeof p.cloud_ts_norm === 'number') ? p.cloud_ts_norm : null,
    app_version: getAppVersion(),
    device_id: getDeviceId(),
    details,
  };
};

// Always-on anomaly capture. Gate-INDEPENDENT: persists + queues even when the
// main telemetry gate is off (brief §Part1.4). Only allowlisted event types are
// persisted. Wrapped so it never throws into the sync critical path.
const logAnomaly = (event, payload) => {
  try {
    if (!ANOMALY_ALLOWLIST.has(event)) return;
    const safe = redact(payload);           // PII strip + id hash (reused)
    const serverEvent = buildServerEvent(event, safe);
    const record = {
      t: Date.now(),
      event,
      payload: safe,        // for on-device inspection (window.__track1.anomalies())
      serverEvent,          // pre-built contract payload queued for the sink
    };
    const arr = readAnomalies();
    arr.push(record);
    writeAnomalies(arr);
    // Also drop into the in-memory ring when the gate is on (parity with log()).
    if (IS_TRACK1_ON()) {
      try { push({ t: record.t, event, payload: safe }); } catch (_) { /* noop */ }
    }
  } catch (_) { /* telemetry must never crash the caller */ }
};

// Fire-and-forget POST of the batched anomaly queue to the server sink. Drains
// `sr.track1.anomalies` ONLY on a successful (2xx) response so a dropped batch
// is retried next boot / reconnect. NEVER awaited in the sync critical path;
// all errors swallowed. Returns a promise (ignored by callers).
let flushInFlight = false;
const flush = async () => {
  try {
    if (flushInFlight) return;              // single-flight — no retry storm
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    const queued = readAnomalies();
    if (!queued.length) return;
    const apiBase = getApiBase();
    const token = getAuthToken();
    if (!apiBase || !token) return;         // no sink / not authed — try later
    if (typeof fetch === 'undefined') return;

    flushInFlight = true;
    // Batch cap per server contract (≤100). Take the oldest slice first.
    const batch = queued.slice(0, ANOMALY_FLUSH_BATCH);
    const events = batch.map((r) => r.serverEvent).filter(Boolean);
    let ok = false;
    try {
      const resp = await fetch(`${apiBase}${ANOMALY_SINK_PATH}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ events }),
      });
      ok = !!(resp && resp.ok);
    } catch (_) {
      ok = false;                           // network error — keep the queue
    }
    if (ok) {
      // Drain exactly the batch we shipped; re-read in case new anomalies were
      // appended during the in-flight POST.
      const current = readAnomalies();
      writeAnomalies(current.slice(batch.length));
    }
  } catch (_) {
    /* swallow — flush is fire-and-forget */
  } finally {
    flushInFlight = false;
  }
};

const clearAnomalies = () => {
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(ANOMALY_LS_KEY);
  } catch (_) { /* noop */ }
};

// Gate-independent reader installed at boot. Reading LS is PII-safe + zero-cost,
// so unlike window.__track1 (gated), this is ALWAYS available for investigation.
const installAnomalyReader = () => {
  try {
    if (typeof window === 'undefined') return;
    // Merge onto any existing __track1anomaly namespace without clobbering.
    window.__track1anomaly = Object.freeze({
      list: readAnomalies,
      clear: clearAnomalies,
      flush: () => { flush(); },     // manual drain trigger for investigators
      deviceId: getDeviceId,
    });
  } catch (_) { /* noop */ }
};

// Per-save fan-out counter - coalesces the ~30 sync-pushes-per-save into one
// event. Consumer calls beginSave() at modal.onSave entry, increments via
// bumpFanOut() in the fan-out path, and endSave() at completion which emits
// the final counter event.
let currentSaveCtx = null;

const beginSave = (label, meta) => {
  if (!IS_TRACK1_ON()) return;
  try {
    currentSaveCtx = { label, meta: redact(meta), startedAt: Date.now(), fanOutCount: 0 };
  } catch (_) { /* noop */ }
};

const bumpFanOut = () => {
  if (!IS_TRACK1_ON()) return;
  try {
    if (currentSaveCtx) currentSaveCtx.fanOutCount += 1;
  } catch (_) { /* noop */ }
};

const endSave = (outcome) => {
  if (!IS_TRACK1_ON()) return;
  try {
    if (currentSaveCtx) {
      push({
        t: Date.now(),
        event: '[SYNC-FAN-OUT]',
        label: currentSaveCtx.label,
        durationMs: Date.now() - currentSaveCtx.startedAt,
        fanOutCount: currentSaveCtx.fanOutCount,
        meta: currentSaveCtx.meta,
        outcome: outcome || null,
      });
      currentSaveCtx = null;
    }
  } catch (_) { /* noop */ }
};

// Primary log entry - call from any instrumented site.
const log = (event, payload) => {
  if (!IS_TRACK1_ON()) return;
  try {
    push({ t: Date.now(), event, payload: redact(payload) });
  } catch (_) { /* noop */ }
};

// Hash exposed for call sites that want id-only signal.
const hashId = (id) => {
  try { return fnv1a(id); } catch (_) { return '00000000'; }
};

// Network + SW state snapshots - cheap.
const captureNetworkState = () => {
  try {
    if (typeof navigator === 'undefined') return { __err: 'no-navigator' };
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return {
      onLine: !!navigator.onLine,
      effectiveType: conn ? conn.effectiveType : null,
      rtt: conn ? conn.rtt : null,
      downlink: conn ? conn.downlink : null,
    };
  } catch (_) {
    return { __err: 'net-capture-failed' };
  }
};

const captureSwState = async () => {
  try {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return { supported: false };
    }
    const reg = await navigator.serviceWorker.getRegistration();
    return {
      supported: true,
      hasController: !!navigator.serviceWorker.controller,
      hasRegistration: !!reg,
      installing: !!(reg && reg.installing),
      waiting: !!(reg && reg.waiting),
      active: !!(reg && reg.active),
    };
  } catch (e) {
    return { __err: 'sw-capture-failed', msg: truncateString(String(e && e.message)) };
  }
};

// hydrationEpoch - module-level counter incremented by useMaintenanceJobsDomain
// on every setValue. PlannedMaintenanceTab captures this on modal-open and again
// at save; if different -> [STALE-EDITING-JOB] fires.
let hydrationEpoch = 0;
const bumpHydrationEpoch = () => { hydrationEpoch += 1; };
const readHydrationEpoch = () => hydrationEpoch;

// sr:storage:set receipt counter - installed via init() at boot only when gate is on.
let srStorageSetReceipts = 0;
const initStorageSetTracking = () => {
  if (!IS_TRACK1_ON() || typeof window === 'undefined') return;
  try {
    window.addEventListener('sr:storage:set', () => {
      srStorageSetReceipts += 1;
    });
  } catch (_) { /* noop */ }
};
const readStorageSetCounts = () => ({ receipts: srStorageSetReceipts });

// Reviewer B 2026-07-02: SYNC-HYDRATE counter replaces per-event ring-buffer push
// to prevent flooding. Advisor reads via readSyncHydrateCounts() when investigating.
let syncHydrateCount = 0;
const syncHydrateByModule = {};
const readSyncHydrateCounts = () => ({
  total: syncHydrateCount,
  byModule: { ...syncHydrateByModule },
});

// Boot-level sr:sync:hydrate listener - per canonical brief B.1, this replaces
// direct edits to UniversalSyncBridge.js. Fires [CLOUD-PULL-SCHEMA] (sampled)
// for maintenance_jobs pulls; also fires a lightweight [SYNC-HYDRATE] counter.
const initSyncHydrateTracking = () => {
  if (!IS_TRACK1_ON() || typeof window === 'undefined') return;
  try {
    window.addEventListener('sr:sync:hydrate', (evt) => {
      try {
        const detail = evt && evt.detail;
        if (!detail) return;
        const moduleName = detail.module;
        // Only sample maintenance_jobs for schema logging.
        if (moduleName === 'maintenance_jobs' && Math.random() < 0.1) {
          const value = detail.value;
          const firstItem = Array.isArray(value) ? value[0]
                          : (value && Array.isArray(value.items)) ? value.items[0]
                          : null;
          push({
            t: Date.now(),
            event: '[CLOUD-PULL-SCHEMA]',
            payload: {
              module: moduleName,
              source: detail.source || null,
              arrayLen: Array.isArray(value) ? value.length
                      : (value && Array.isArray(value.items)) ? value.items.length : null,
              fieldNames: firstItem && typeof firstItem === 'object'
                        ? Object.keys(firstItem).slice(0, 32) : null,
            },
          });
        }
        // Reviewer B 2026-07-02: was per-event push; converted to counter to
        // prevent ring-buffer flooding (~30 modules × frequent hydrates
        // would evict the diagnostic events we're actually trying to catch).
        syncHydrateCount++;
        if (moduleName) {
          syncHydrateByModule[moduleName] = (syncHydrateByModule[moduleName] || 0) + 1;
        }
      } catch (_) { /* noop */ }
    });
  } catch (_) { /* noop */ }
};

// Initialisation - call ONCE from boot code (TailwindApp mount).
const init = () => {
  // FT-379: gate-INDEPENDENT always-on anomaly path. These run even when the
  // main telemetry gate is off: reading LS is PII-safe + zero-cost, and a
  // data-loss anomaly must be captured + drained regardless of the gate.
  try { installAnomalyReader(); } catch (_) { /* noop */ }
  // Opportunistic drain on boot (brief §Part1: flush trigger = next boot).
  // Fire-and-forget — never awaited; swallows all errors internally.
  try { flush(); } catch (_) { /* noop */ }

  if (!IS_TRACK1_ON()) return;
  try {
    initStorageSetTracking();
    initSyncHydrateTracking();
    // Boot SW state snapshot.
    captureSwState().then(state => {
      push({ t: Date.now(), event: '[SW-STATE]', payload: state });
    }).catch(() => { /* noop */ });
    // Boot net state snapshot.
    push({ t: Date.now(), event: '[NET-STATE-BOOT]', payload: captureNetworkState() });
    // Expose dump helper - only when gate is on.
    if (typeof window !== 'undefined') {
      window.__track1 = Object.freeze({
        dump: () => {
          const warn = 'STAGING TELEMETRY - DO NOT SHARE. Contains hashed but potentially correlatable identifiers.\n';
          try {
            return warn + JSON.stringify(buffer, null, 2);
          } catch (_) {
            return warn + '{"error":"dump-failed"}';
          }
        },
        length: () => buffer.length,
        clear: () => { buffer.length = 0; },
        counts: readStorageSetCounts,
        hydrateCounts: readSyncHydrateCounts,
        // FT-379: anomaly slice retrieval helpers (also on window.__track1anomaly,
        // which is gate-independent). Returns the persisted LS slice.
        anomalies: readAnomalies,
        clearAnomalies,
        flushAnomalies: () => { flush(); },
      });
    }
  } catch (_) { /* noop */ }
};

// Test-only helpers - not part of the frozen public API but exported for
// unit tests to reset ring-buffer + epoch state between cases.
const __resetForTests = () => {
  buffer.length = 0;
  hydrationEpoch = 0;
  srStorageSetReceipts = 0;
  syncHydrateCount = 0;
  Object.keys(syncHydrateByModule).forEach(k => { delete syncHydrateByModule[k]; });
  currentSaveCtx = null;
  flushInFlight = false;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(ANOMALY_LS_KEY);
      localStorage.removeItem(ANOMALY_DEVICE_ID_KEY);
    }
  } catch (_) { /* noop */ }
};
const __peekBuffer = () => buffer.slice();

// Named export - stable identity (never inside useMemo dep list).
export const track1 = Object.freeze({
  log,
  logAnomaly,        // FT-379: always-on anomaly capture (gate-independent)
  flush,             // FT-379: fire-and-forget sink drain
  readAnomalies,     // FT-379
  clearAnomalies,    // FT-379
  getDeviceId,       // FT-379
  beginSave,
  bumpFanOut,
  endSave,
  hashId,
  captureNetworkState,
  bumpHydrationEpoch,
  readHydrationEpoch,
  readStorageSetCounts,
  readSyncHydrateCounts,
  init,
  isOn: IS_TRACK1_ON,
});

// Exports for tests only.
export { redact, __resetForTests, __peekBuffer, buildServerEvent, getApiBase, ANOMALY_SINK_PATH, ANOMALY_LS_KEY, ANOMALY_LS_CAP };

export default track1;
